import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import OfficerShell from "./OfficerShell";

const ORG_CLS = {
  "ग्रामपंचायत": "bg-emerald-50 text-emerald-700",
  "नगरपंचायत": "bg-amber-50 text-amber-700",
  "नगरपरिषद": "bg-blue-50 text-blue-700",
  "महानगरपालिका": "bg-purple-50 text-purple-700",
};

const Stat = ({ label, value, cls }) => (
  <div className={`rounded-2xl border border-[#e6dcc4] bg-white px-5 py-4 ${cls || ""}`} data-testid={`vac-stat-${label}`}>
    <p className="text-[12.5px] font-semibold text-slate-500">{label}</p>
    <p className="mt-1 font-head text-[28px] font-medium text-[#1b3d2a]">{value}</p>
  </div>
);

export default function VacancyDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true); setErr("");
    try { setData(await api.vacancySummary()); }
    catch (e) { setErr(e?.message || "माहिती लोड करता आली नाही."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const t = data?.totals || { sanctioned: 0, filled: 0, remaining: 0, bodies: 0 };

  return (
    <OfficerShell>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-head text-[24px] font-medium text-[#1b5e3f]">रिक्त पदे / भरती स्थिती</h1>
          <p className="text-[13px] text-slate-500">मंजूर केंद्रे (Sanctioned) वि. भरलेली (मंजूर अर्ज) — तालुका व संस्थेनुसार</p>
        </div>
        <button onClick={load} data-testid="vac-refresh" className="flex items-center gap-1.5 rounded-full border border-[#1b5e3f]/30 px-3.5 py-1.5 text-[13px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/5"><RefreshCw className="h-4 w-4" /> पुन्हा लोड</button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400">माहिती लोड होत आहे...</div>
      ) : err ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-[#e6dcc4] bg-white p-10 text-slate-500">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-[14px] font-semibold text-red-600">{err}</p>
          <button onClick={load} className="rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[13px] font-semibold text-white">पुन्हा प्रयत्न करा</button>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="एकूण मंजूर केंद्रे" value={t.sanctioned} />
            <Stat label="भरलेली" value={t.filled} />
            <Stat label="रिक्त" value={t.remaining} />
            <Stat label="एकूण संस्था" value={t.bodies} />
          </div>

          <div className="overflow-hidden rounded-2xl border border-[#e6dcc4] bg-white">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-slate-200 bg-[#fbf9f2] text-left text-slate-500">
                  <th className="p-3">तालुका</th>
                  <th className="p-3 text-center">मंजूर केंद्रे</th>
                  <th className="p-3 text-center">भरलेली</th>
                  <th className="p-3 text-center">रिक्त</th>
                  <th className="p-3 text-center">संस्था</th>
                </tr>
              </thead>
              <tbody>
                {(data?.talukas || []).length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">कोणतीही माहिती उपलब्ध नाही.</td></tr>
                )}
                {(data?.talukas || []).map((g) => (
                  <React.Fragment key={g.taluka}>
                    <tr className="cursor-pointer border-b border-slate-100 hover:bg-[#faf7ee]" data-testid={`vac-taluka-${g.taluka}`}
                        onClick={() => setOpen((o) => ({ ...o, [g.taluka]: !o[g.taluka] }))}>
                      <td className="p-3 font-semibold text-[#1b3d2a]">
                        <span className="inline-flex items-center gap-1.5">
                          {open[g.taluka] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {g.tehsilMr}
                        </span>
                      </td>
                      <td className="p-3 text-center font-semibold">{g.sanctioned}</td>
                      <td className="p-3 text-center text-emerald-700">{g.filled}</td>
                      <td className="p-3 text-center font-bold text-[#c2570a]">{g.remaining}</td>
                      <td className="p-3 text-center text-slate-500">{g.bodies.length}</td>
                    </tr>
                    {open[g.taluka] && (
                      <tr><td colSpan={5} className="bg-[#fcfbf6] p-0">
                        <table className="w-full text-[12.5px]">
                          <thead><tr className="text-left text-slate-400">
                            <th className="py-2 pl-10">संस्था</th><th className="py-2">प्रकार</th>
                            <th className="py-2 text-center">मंजूर</th><th className="py-2 text-center">भरलेली</th><th className="py-2 pr-4 text-center">रिक्त</th>
                          </tr></thead>
                          <tbody>
                            {g.bodies.map((b, i) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="py-1.5 pl-10">{b.body}{b.ward ? ` · ${b.ward}` : ""}</td>
                                <td className="py-1.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ORG_CLS[b.orgType] || "bg-slate-100 text-slate-600"}`}>{b.orgType}</span></td>
                                <td className="py-1.5 text-center font-semibold">{b.sanctioned}</td>
                                <td className="py-1.5 text-center text-emerald-700">{b.filled}</td>
                                <td className="py-1.5 pr-4 text-center font-bold text-[#c2570a]">{b.remaining}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </OfficerShell>
  );
}
